import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import apiClient from '../shared/api/apiClient';

interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  loading: boolean;
}

type AuthAction =
  | { type: 'LOGIN_SUCCESS'; payload: AuthUser }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean };

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: true,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return { ...state, isAuthenticated: true, user: action.payload, loading: false };
    case 'LOGOUT':
      return { ...state, isAuthenticated: false, user: null, loading: false };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    default:
      return state;
  }
};

const AuthContext = createContext<{
  state: AuthState;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
} | null>(null);

const TOKEN_KEY = 'textflow_auth_token';
const USER_KEY = 'textflow_user_data';

function clearStoredSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  // Clear cross-subdomain cookies
  document.cookie = `${TOKEN_KEY}=; domain=.textflow.ca; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  document.cookie = `${USER_KEY}=; domain=.textflow.ca; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

function getStoredToken(): string | null {
  // Check localStorage first
  const lsToken = localStorage.getItem(TOKEN_KEY);
  if (lsToken) return lsToken;

  // Fall back to cookies (cross-subdomain SSO from textflow.ca login)
  const cookies = document.cookie.split(';');
  const tokenCookie = cookies.find(c => c.trim().startsWith(`${TOKEN_KEY}=`));
  const userCookie = cookies.find(c => c.trim().startsWith(`${USER_KEY}=`));

  if (tokenCookie && userCookie) {
    const token = tokenCookie.split('=').slice(1).join('=').trim();
    const userData = decodeURIComponent(userCookie.split('=').slice(1).join('=').trim());
    // Transfer to localStorage and clear cookies
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, userData);
    document.cookie = `${TOKEN_KEY}=; domain=.textflow.ca; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `${USER_KEY}=; domain=.textflow.ca; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    return token;
  }

  return null;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // On startup, verify any stored token against the backend
  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }

    apiClient.get('/auth/verify', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res: any) => {
        const user = res.data?.user ?? res.user;
        dispatch({ type: 'LOGIN_SUCCESS', payload: user });
      })
      .catch(() => {
        // Token invalid or expired — force re-login
        clearStoredSession();
        dispatch({ type: 'SET_LOADING', payload: false });
      });
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const res = await apiClient.post<{ token: string; user: AuthUser }>(
        '/auth/login',
        { email, password }
      );
      const { token, user } = (res as any).data ?? res;
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      dispatch({ type: 'LOGIN_SUCCESS', payload: user });
      return true;
    } catch {
      dispatch({ type: 'SET_LOADING', payload: false });
      return false;
    }
  };

  const logout = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      apiClient.post('/auth/logout', {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    clearStoredSession();
    dispatch({ type: 'LOGOUT' });
    window.location.href = 'https://textflow.ca';
  };

  return (
    <AuthContext.Provider value={{ state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
